import * as React from 'react';
import { EventFragment, Layout, EventPageType, EventFragment_pages } from '@graphql/types';
import produce, { DraftObject } from 'immer';
import {
  PreviewInternalAction,
  PreviewInitializationSource,
  PreviewMessage,
  PreviewActions,
  PreviewProperty
} from './types';
import { makeDebugger } from '@lib';
import { themeService } from '@guestServices/theme/themeService';

const debug = makeDebugger('<PreviewListener/>');
import { supportedFontFamily } from '@routes/event/guest/services/fonts/fontService';
import { DEVICE_DIMENSIONS } from '@routes/event/create/DesignDemo/constants';
import { RouteComponentProps } from 'react-router';
import { scrollToYCasually } from 'utils/scrollToY';
import { NewThemeSettings, INITIAL_THEME_SETTINGS } from 'utils/themeContext/ThemeContext';
import { BrannanProvider } from 'utils/themeContext/ThemeContext';

type RenderCallback = (
  args: {
    event: EventFragment;
    // history: RouteComponentProps<any>['history'];
    // location: RouteComponentProps<any>['location'];
  }
) => JSX.Element;

interface Props {
  children: RenderCallback;
  event: EventFragment;
}

interface State {
  event: EventFragment;
  themeSettings: NewThemeSettings;
}

const PREVIEW_ACTIONS: (PreviewActions)[] = [
  'ownerFirstName',
  'fianceeFirstName',
  'layout',
  'theme',
  'color',
  'font',
  'location',
  'eventDate',
  'suggestedColor',
  'backgroundType',
  'enableBackgroundTranslucency',
  'fromList',
  'assetId',
  'greeting',
  'coverPhoto',
  'currentPageType',
  PreviewInitializationSource.searchParams,
  PreviewInitializationSource.onInitialized
];
const PREVIEW_ACTIONS_SET: Set<PreviewActions> = new Set(PREVIEW_ACTIONS);

const isSupportedPageType = (page: string | null): page is EventPageType => {
  return page ? page in EventPageType : false;
};

// const TARGET_ORIGIN = window.config().host!;

export enum IframeSource {
  joyPreview = 'joyPreview'
}

export class PreviewListenerWithRouter extends React.Component<Props & Omit<RouteComponentProps<any>, 'match'>, State> {
  private targetOrigin: string;

  private intercomNode: HTMLElement | null = null;

  private welcomeSectionNode: HTMLElement | null = null;
  private travelSectionNode: HTMLElement | null = null;

  private resetCoverPhoto: boolean = false;
  private activeCoverPhotoSection: string | null = null;

  state = {
    event: this.props.event,
    themeSettings: INITIAL_THEME_SETTINGS(this.props.event)
  };

  constructor(props: Props & RouteComponentProps<any>) {
    super(props);
    this.targetOrigin = window.config().host!;
  }

  componentDidMount() {
    this.collectSectionNodes();
    this.checkIfInsideIframe();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const previousEvent = prevState.event;
    const currentEvent = this.state.event;
    const hasLayoutChanged = previousEvent.style.layout !== currentEvent.style.layout;
    this.collectSectionNodes(hasLayoutChanged);

    if (hasLayoutChanged) {
      this.handleLayoutChange();
    } else if (previousEvent.style.theme !== currentEvent.style.theme) {
      this.handleThemeChange();
    } else if (previousEvent.style.font !== currentEvent.style.font) {
      scrollToYCasually(0);
    } else if (previousEvent.style.primaryColor !== currentEvent.style.primaryColor) {
      this.handleColorChange();
    } else if (previousEvent.style.suggestedColor !== currentEvent.style.suggestedColor) {
      this.handleSuggestedColorChange();
    }

    if (this.resetCoverPhoto) {
      this.resetCoverPhoto = false;
      this.setScrollToCurrentCoverPhotoSection();
    }
  }

  componentWillUnmount() {
    debug('componentWillUnmount()');
    this.toggleIntercom(true);
  }

  collectSectionNodes = (reset = false) => {
    if (window.self !== window.top) {
      this.welcomeSectionNode = reset
        ? document.getElementById('welcome')
        : this.welcomeSectionNode || document.getElementById('welcome');
      this.travelSectionNode = reset
        ? document.getElementById('travel')
        : this.travelSectionNode || document.getElementById('travel');
    }
  };

  checkIfInsideIframe = () => {
    if (window.self !== window.top) {
      this.intercomNode = document.getElementById('intercom-container');
      this.toggleIntercom(false);

      setTimeout(() => {
        this.listenForPostmessage();
        window.top.postMessage(this.buildInitializedMessage(), this.targetOrigin);
      }, 500);
    }
  };

  listenForPostmessage = () => {
    window.addEventListener('message', this.recieveMessage, false);
  };

  recieveMessage = (event: MessageEvent) => {
    debug('receiveMessage()');
    if (event.origin !== this.targetOrigin) {
      return;
    } else {
      if (!!event.data) {
        try {
          this.processMessage(JSON.parse(event.data));
        } catch (e) {
          debug(e);
        }
      }
    }
  };

  processMessage = (message: PreviewMessage) => {
    if (PREVIEW_ACTIONS_SET.has(message.action)) {
      this.collectSectionNodes();

      this.setState(
        produce<State>(draft => {
          if (
            message.action === PreviewInitializationSource.searchParams ||
            message.action === PreviewInitializationSource.onInitialized ||
            message.action === 'fromList'
          ) {
            if (message.value instanceof Array) {
              message.value.forEach(({ key, value }) => {
                this.updateStateBasedOnMessage(draft, key, value);
              });
            }
          } else if (typeof message.value === 'string' || typeof message.value === 'boolean') {
            this.updateStateBasedOnMessage(draft, message.action, message.value);
          }
        }),
        () => {
          const activeCoverPhotoSection = this.activeCoverPhotoSection;
          if (typeof activeCoverPhotoSection === 'string') {
            const page = this.state.event.pages.find(p => p.type === activeCoverPhotoSection);
            if (page) {
              if (!this.props.location.pathname.includes(page.pageSlug)) {
                this.props.history.replace(
                  `/${this.state.event.website}/${page.pageSlug}${this.props.location.search}`
                );
              }

              if (this.state.event.style.layout === 'aloha') {
                const mainPanel = document.getElementById('detailsPanel');
                const section = document.getElementById(activeCoverPhotoSection);
                if (section && mainPanel) {
                  const sectionOffset = section.offsetTop;
                  mainPanel.style.overflowY = 'hidden';
                  requestAnimationFrame(() => {
                    mainPanel.scrollTop = sectionOffset;
                  });
                }
              }
            }
          }
        }
      );
    }
  };

  render() {
    return (
      <BrannanProvider value={this.state.themeSettings}>
        {this.props.children({
          event: this.state.event
        })}
      </BrannanProvider>
    );
  }

  private setScrollToCurrentCoverPhotoSection = () => {
    if (this.state.event.style.layout === 'aloha' && this.activeCoverPhotoSection) {
      this.activeCoverPhotoSection = null;
      const mainPanel = document.getElementById('detailsPanel');
      if (mainPanel) {
        mainPanel.style.overflowY = null;
        requestAnimationFrame(() => {
          this.props.history.replace(`/${this.state.event.website}/welcome${this.props.location.search}`);
        });
      }
    }
  };

  private updateStateBasedOnMessage = (draft: DraftObject<State>, action: PreviewProperty, value: any): void => {
    debug(`updateStateBasedOnMessage(); action: ${action}, value: ${value}`);
    switch (action) {
      case 'currentPageType':
        this.activeCoverPhotoSection = value;
        break;
      case 'ownerFirstName':
        draft.event.info.ownerFirstName = value;
        break;
      case 'fianceeFirstName':
        draft.event.info.fianceeFirstName = value;
        break;
      case 'eventDate':
        draft.event.info.date = value;
        break;
      case 'location':
        draft.event.info.location = value;
        break;
      case 'color':
        draft.event.style.primaryColor = value;
        break;
      case 'suggestedColor':
        draft.themeSettings.customBackgroundColor.isEnabled = !Boolean(value);
        break;
      case 'assetId':
        if (draft.event.pages[0].photo) {
          draft.event.pages[0].photo.url = value;
        }
        break;
      case 'coverPhoto': {
        if (typeof value === 'object') {
          const { page, action: updateAction, asset } = value;
          if (isSupportedPageType(page) && typeof asset === 'object') {
            let targetPage: EventFragment_pages | undefined = draft.event.pages.find(
              currentPage => currentPage.type === page
            );

            if (targetPage) {
              targetPage.photo =
                asset.url === null
                  ? null
                  : {
                      __typename: 'Photo',
                      id: 'preview',
                      assetId: asset.assetId,
                      url: asset.url,
                      height: asset.height,
                      width: asset.width,
                      container: this.props.event.eventId!
                    };
              this.activeCoverPhotoSection = page;
            }

            if (updateAction === 'reset') {
              this.resetCoverPhoto = true;
            }
          }
        }
        break;
      }
      case 'greeting':
        draft.event.pages[0].pageSubTitle = value;
        break;
      case 'theme':
        draft.event.style.theme = value;

        // 1. If theme is ours + is an elli theme that the couple was grandfathered into, use the config we currently have.
        // 2. If new nelli theme, the following values are set to false so that the applicator can do it's thang.
        // draft.event.style.translucent = this.checkSupportsTranslucentEffect(value);
        // draft.event.style.border = this.checkSupportsTranslucentEffect(value) ? false : true;
        break;
      case 'backgroundType':
        draft.themeSettings.backgroundType = value;
        break;
      case 'enableBackgroundTranslucency':
        draft.themeSettings.backgroundTranslucency.isEnabled = Boolean(value);
        break;
      case 'font':
        window.WebFont.load({
          google: {
            families: [supportedFontFamily(value)]
          }
        });
        draft.event.style.font = value;
        break;
      case 'template':
      case 'layout':
        switch (value) {
          case 'aloha':
            draft.event.style.layout = Layout.aloha;
            draft.themeSettings.customBackgroundColor.isEnabled = false;
            break;
          case 'brannan':
            draft.event.style.layout = Layout.brannan;

            // Maps to `useSuggestedBackgroundColor = true`;
            draft.themeSettings.customBackgroundColor.isEnabled = false;
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    if (action === 'ownerFirstName' || action === 'fianceeFirstName') {
      const { ownerFirstName, fianceeFirstName } = draft.event.info;
      draft.event.info.eventDisplayName = `${ownerFirstName || 'Romeo'} & ${fianceeFirstName || 'Juliet'}`;
    }
  };

  private buildInitializedMessage = () => {
    return {
      action: PreviewInternalAction.previewInitialized,
      source: IframeSource.joyPreview,
      payload: JSON.stringify(this.state.event)
    };
  };

  private handleLayoutChange = () => {
    switch (this.state.event.style.layout) {
      case Layout.aloha: {
        if (window.innerWidth <= DEVICE_DIMENSIONS.phone.width) {
          this.attemptToScrollToNode('welcome');
        } else {
          scrollToYCasually(0);
        }
        break;
      }
      case Layout.brannan: {
        scrollToYCasually(0);
        break;
      }
      default:
        break;
    }
  };

  private handleThemeChange = () => {
    switch (this.state.event.style.layout) {
      case Layout.aloha: {
        if (window.innerWidth <= DEVICE_DIMENSIONS.phone.width) {
          // Is Mobile display - scroll to welcome page
          this.attemptToScrollToNode('welcome');
        } else {
          scrollToYCasually(0);
        }
        break;
      }
      case Layout.brannan: {
        scrollToYCasually(0);
        break;
      }
      default:
        break;
    }
  };

  private handleColorChange = () => {
    switch (this.state.event.style.layout) {
      case Layout.aloha: {
        const themeConfig = themeService(this.state.event.style.theme, Layout.aloha);
        if (!themeConfig.supportsWeddingColor) {
          this.attemptToScrollToNode('travel');
        }
        break;
      }
      default:
        break;
    }
  };

  private handleSuggestedColorChange = () => {
    switch (this.state.event.style.layout) {
      case Layout.aloha: {
        if (window.innerWidth <= DEVICE_DIMENSIONS.phone.width) {
          this.attemptToScrollToNode('welcome');
        } else {
          scrollToYCasually(0);
        }
        break;
      }
      default:
        break;
    }
  };

  private toggleIntercom = (show: boolean) => {
    if (this.intercomNode) {
      this.intercomNode.classList.toggle('hidden', !show);
    }
  };

  private attemptToScrollToNode = (section: 'travel' | 'welcome') => {
    let targetSectionNode = section === 'travel' ? this.travelSectionNode : this.welcomeSectionNode;
    if (targetSectionNode) {
      scrollToYCasually(targetSectionNode.offsetTop);
    }
  };
}

export const PreviewListener = PreviewListenerWithRouter;
